import { AccessRight } from "./AccessControlTypes";
import { NegotiationStatus, RawLever } from "./NegotiationTypes";
import type { Vendor } from "./VendorTypes";

export enum OrderFormStatus {
    EXPIRATION_NOT_SET = "not_set",
    RENEWAL_SCHEDULED = "renewal_scheduled",
    WILL_NOT_RENEW = "will_not_renew",
    OVERDUE = "overdue",
    RENEWED = "renewed",
}

export enum OrderFormProcessingStatus {
    NONE = "none",
    PROCESSING = "processing",
    COMPLETE = "complete",
    CANCELLED = "cancelled",
}

export interface OrderForm {
    id: number;
    name: string;
    status: OrderFormStatus;
    negotiationStatus: NegotiationStatus;
    orderStartDate?: Date;
    recordCreationDate: Date;
    orderEndDate?: Date;
    migrationLength?: number;
    value: number;
    autoRenewal: boolean;
    vendorKey: string;
    vendor?: Vendor;
    creatorId: number;
    ownerId: number;
    businessOwnerEmail: string;
    processingStatus: OrderFormProcessingStatus;
    dealRating: DealRating;
    category: string;
    legalWeight: number;
    centsValue: number;
    utilizationScore: number;
    orderScore: number;
    increase?: number;
    decrease?: number;
    optimizedScore: number;
    contractType?: ContractType;
    projectId?: number;
    // "enriched"
    topNegotiationStrategies?: string;
}

export interface OrderFormSKU {
    id: number;
    orderFormId: number;
    name: string;
    sku: string;
    quantity: number;
    price: number;
    totalPrice: number;
    usage: number;
}

export interface OrderFormResponse {
    orderForm: OrderForm;
    access: AccessRight;
}

export interface OrderFormUpdateResponse extends OrderForm {}

export enum DealRating {
    A = 1,
    B = 2,
    C = 3,
    D = 4,
    E = 5,
    F = 6,
    NONE = 0,
}
export enum OFOPerationType {
    ADD = "ADD",
    REMOVE = "REMOVE",
}

export interface OrderFormGroupOperation {
    type: OFOPerationType;
    values: number[];
}

export interface OrderFormGroupOperationResponse {
    type: OFOPerationType;
    values: number[];
}

export interface OrderFormLever {
    id: number;
    leverId: number;
    orderFormId: number;
    selectedOptionId: number;
    recordCreationDate: Date;
}

export interface OrderFormLeverDetail extends OrderFormLever {
    isSelected: boolean;
    lever: RawLever;
}

export interface OrderFormAnalysis {
    id: number;
    orderFormId: number;
    analysisId: number;
    selectedOptionId: number;
}

export interface Analysis {
    id: number;
    title: string;
    description: string;
    score: number;
    options: AnalysisOption[];
}

export interface AnalysisOption {
    id: number;
    title: string;
    weight: number;
}

export interface OrderFormAnalysisResponse extends Partial<OrderFormAnalysis> {
    analysis: Analysis;
}

export enum ContractType {
    MSA = "MSA",
    Order = "Order",
    Amendment = "Amendment",
    Other = "Other",
    IncorporatedElsewhere = "IncorporatedElsewhere",
}

export enum DynamicLeverEvaluation {
    FAVORABLE = "Favorable",
    NEUTRAL = "Neutral",
    NOT_FAVORABLE = "Not Favorable",
    NOT_PRESENT = "Not Present",
}

export interface OrderFormDynamicLever {
    id: number;
    orderFormId: number;
    recordCreationDate: Date;
    name?: string;
    type?: string;
    presentInContract?: boolean; // whether the lever is present in the contract (which can be a favorable thing or not)
    evaluation?: DynamicLeverEvaluation;
    currentState?: string;
    isActionable: boolean; // whether the lever is NOT in good standing, i.e. actionable
    priority?: number; // priority for levers not in good standing
    optimalState?: string; // optimal state for levers not in good standing
    justification?: string; // justification for levers not in good standing
}
